/**
 * Color coding utilities for technician and stage-based job coloring.
 * Used in Today view (tech colors) and Calendar view (stage/tech toggle).
 */

// 8 curated muted wash/accent pairs
// theme-ignore-start — data: calendar palette and default stage colours
export const COLOR_PALETTE = [
  { name: 'Blue',    wash: '#EFF6FF', accent: '#3B82F6' },
  { name: 'Green',   wash: '#ECFDF5', accent: '#10B981' },
  { name: 'Amber',   wash: '#FFFBEB', accent: '#F59E0B' },
  { name: 'Red',     wash: '#FEF2F2', accent: '#EF4444' },
  { name: 'Purple',  wash: '#F5F3FF', accent: '#8B5CF6' },
  { name: 'Teal',    wash: '#F0FDFA', accent: '#14B8A6' },
  { name: 'Pink',    wash: '#FDF2F8', accent: '#EC4899' },
  { name: 'Indigo',  wash: '#EEF2FF', accent: '#6366F1' },
];

// Default stage colors — keyed by requests.current_state (job lifecycle)
export const DEFAULT_STAGE_COLORS = {
  scheduled: '#3B82F6',
  received:  '#8B5CF6',
  started:   '#F59E0B',
  finished:  '#10B981',
  collected: '#EC4899',
};
// theme-ignore-end

// Kept local (not imported from util/theme.js) so this module stays import-free
// for the Node test harness.
const activeTheme = () => (
  typeof document !== 'undefined' && document.documentElement.getAttribute('data-theme') === 'dark'
    ? 'dark' : 'light'
);

/**
 * Look up the wash color for a given accent hex.
 * Light: the pale wash from the palette, or a faint tint of a custom accent.
 * Dark: a dark tint of the accent (pale washes glow on a near-black page). The
 * accent itself — the colour the shop chose — is never changed.
 */
export function getWashFromAccent(accent, theme = activeTheme()) {
  if (!accent) return null;
  if (theme === 'dark') {
    return /^#[0-9a-fA-F]{6}$/.test(accent) ? `${accent}33` : 'var(--color-bg-inset)';
  }
  const match = COLOR_PALETTE.find(c => c.accent.toLowerCase() === accent.toLowerCase());
  return match ? match.wash : accent + '18'; // fallback: accent with low opacity
}

/**
 * Get technician color from the staff/technicians list.
 * Matches by ID first, then by name as fallback (calendar data may only have name).
 * Returns { wash, accent } or null if no color is set.
 */
export function getTechnicianColor(techIdOrName, technicians) {
  if (!techIdOrName || !Array.isArray(technicians)) return null;
  // Try ID match first
  let tech = technicians.find(t =>
    String(t.User_id) === String(techIdOrName) || String(t.user_id) === String(techIdOrName)
  );
  // Fallback: name match (for calendar data that only has assignedTechnicianName)
  if (!tech && typeof techIdOrName === 'string' && isNaN(techIdOrName)) {
    tech = technicians.find(t =>
      (t.user_name || '').toLowerCase() === techIdOrName.toLowerCase()
    );
  }
  if (!tech || !tech.user_color) return null;
  const accent = tech.user_color;
  const wash = getWashFromAccent(accent);
  return { wash, accent };
}

/**
 * Get stage color for a workflow state.
 * Uses installer's custom stage colors if available, else defaults.
 * Returns { wash, accent } or null if no color for that state.
 */
export function getStageColor(state, installerStageColors) {
  const colors = installerStageColors || {};
  const accent = colors[state] || DEFAULT_STAGE_COLORS[state];
  if (!accent) return null;
  const wash = getWashFromAccent(accent);
  return { wash, accent };
}

// Color used when a task has no assignee — same as the legacy unassigned-event gray.
const UNASSIGNED_ACCENT = '#9CA3AF'; // theme-ignore-line

/**
 * Build the per-tech band segments for a calendar event from a tech_assignment block.
 *
 * Returns null when the event has only one tech (caller should fall back to
 * the existing single-color borderLeft path) or when no tech_assignment is present.
 *
 * Returns { segments: [{ color, name, count }], overflow } otherwise.
 * Segments are capped at 3; overflow is the count of techs not rendered.
 *
 * @param {Object} techAssignment - Server-enriched tech_assignment block
 * @param {Array}  technicians    - Staff list for color/name fallback
 */
export function getEventBands(techAssignment, technicians) {
  if (!techAssignment) return null;

  const state = techAssignment.effective_state;
  if (state === 'single') return null; // caller uses single-color path

  const techs = Array.isArray(techAssignment.techs) ? techAssignment.techs : [];
  const unassignedCount = techAssignment.unassigned_task_count || 0;

  const segments = techs.map((t) => {
    let accent = t.user_color;
    if (!accent) {
      const looked = getTechnicianColor(t.technician_id || t.user_id, technicians);
      accent = looked ? looked.accent : UNASSIGNED_ACCENT;
    }
    return { color: accent, name: t.name, count: t.task_count };
  });

  if ((state === 'partial' || state === 'none') && unassignedCount > 0) {
    segments.push({ color: UNASSIGNED_ACCENT, name: 'Unassigned', count: unassignedCount });
  }

  if (segments.length === 0) {
    return { segments: [{ color: UNASSIGNED_ACCENT, name: 'Unassigned' }], overflow: 0 };
  }
  if (segments.length <= 3) {
    return { segments, overflow: 0 };
  }
  return { segments: segments.slice(0, 3), overflow: segments.length - 3 };
}

/**
 * Build a CSS linear-gradient declaration for an array of band segments.
 * Each segment gets equal vertical share. Used as a borderImage source so
 * the existing 4px-wide left border becomes a multi-color stripe.
 */
export function buildBandGradient(segments) {
  if (!segments || segments.length === 0) return null;
  if (segments.length === 1) return `linear-gradient(to bottom, ${segments[0].color}, ${segments[0].color})`;
  const step = 100 / segments.length;
  const stops = segments.map((seg, i) => {
    const start = (i * step).toFixed(3);
    const end = ((i + 1) * step).toFixed(3);
    return `${seg.color} ${start}% ${end}%`;
  });
  return `linear-gradient(to bottom, ${stops.join(', ')})`;
}
